import { useEffect, useState } from "react";
import { Users, CheckCircle2, Wallet as WalletIcon, LogOut, Wallet } from "lucide-react";
import Logo from "../assets/logos/logo.png";
import { useAuth } from "../context/AuthContext";
import { getReferrerMe, getMyReferrals, getMyEarnings } from "../services/referrerService";
import { getUserWallet } from "../services/walletService";
import StatCard from "../components/StatCard";
import ReferralLinkCard from "../components/ReferralLinkCard";
import HubStatusTable from "../components/HubStatusTable";
import BankingDetailsCard from "../components/BankingDetailsCard";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [referrer, setReferrer] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState({ totalPaid: 0, totalPending: 0, commissions: [] });
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const profile = await getReferrerMe();
      setReferrer(profile);

      const [referralsData, earningsData, walletData] = await Promise.all([
        getMyReferrals(),
        getMyEarnings(),
        user?.id ? getUserWallet(user.id) : Promise.resolve(null),
      ]);

      setReferrals(referralsData);
      setEarnings(earningsData);
      setWallet(walletData);
      setLoading(false);
    })();
  }, [user?.id]);

  const rows = referrals.map((r) => {
    const commission = earnings.commissions.find((c) => c.referralId === r.id);
    return {
      hubName: r.hubName ?? "Unnamed hub",
      status: r.status,
      grossRevenue: commission?.grossTicketRevenue ?? null,
      yourShare: commission?.referrerShareAmount ?? null,
    };
  });

  const qualifiedCount = referrals.filter((r) => r.status === "Qualified").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-base flex items-center justify-center">
        <p className="text-text-muted text-sm">Loading your dashboard…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base relative overflow-hidden">
      {/* Ambient background — same language as Login/Onboarding, so the
          dashboard doesn't feel like a flatter, plainer screen by contrast */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[-120px] left-1/4 w-[600px] h-[350px] rounded-full opacity-[0.07]"
          style={{ background: "radial-gradient(ellipse, #139679 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[-100px] right-0 w-[450px] h-[450px] rounded-full opacity-[0.05]"
          style={{ background: "radial-gradient(ellipse, #4F46E5 0%, transparent 70%)" }}
        />
      </div>

      <header className="relative z-10 bg-bg-topbar border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src={Logo} alt="Ingroove" className="h-6" />
          <span className="text-text-muted text-xs uppercase tracking-widest">
            Referrals
          </span>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-text-muted hover:text-text-primary text-sm transition"
        >
          <LogOut size={15} />
          Log out
        </button>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-text-primary text-2xl font-bold mb-1">
            Welcome back{user?.name ? `, ${user.name}` : ""}
          </h1>
          <p className="text-text-muted text-sm">
            Here's how your referrals are doing.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 mb-8">
          <StatCard icon={Users} label="Hubs referred" value={referrals.length} accent="primary" />
          <StatCard icon={CheckCircle2} label="Qualified" value={qualifiedCount} accent="success" />
          <StatCard
            icon={WalletIcon}
            label="Total earned"
            value={`R${earnings.totalPaid.toFixed(2)}`}
            accent="secondary"
          />
        </div>

        {/* Two-column from lg up: main content left, sticky payout sidebar
            right — uses the width instead of one long centered column */}
        <div className="grid gap-8 lg:grid-cols-[1fr_360px] items-start">
          <div className="space-y-8">
            <ReferralLinkCard referralCode={referrer?.referralCode} />

            <div>
              <h2 className="text-text-primary font-semibold text-sm uppercase tracking-wider mb-4">
                Your referrals
              </h2>
              <HubStatusTable rows={rows} referralLink={`https://ingroove.co.za/referrals?ref=${referrer?.referralCode ?? ""}`} />
            </div>
          </div>

          <div className="lg:sticky lg:top-6 space-y-4">
            <h2 className="text-text-primary font-semibold text-sm uppercase tracking-wider">
              Payout details
            </h2>

            {wallet && (
              <div className="flex items-center gap-3 bg-bg-surface border border-border rounded-2xl p-4">
                <div className="w-9 h-9 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                  <Wallet size={16} className="text-secondary" />
                </div>
                <div>
                  <p className="text-text-muted text-xs">Wallet balance</p>
                  <p className="text-text-primary font-semibold text-sm">
                    R{wallet.availableBalance?.toFixed(2) ?? "0.00"} available
                  </p>
                </div>
              </div>
            )}

            <BankingDetailsCard userId={user?.id} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
